/**
 * CaseStatusBadge コンポーネント
 *
 * FL-142: 案件ステータス機能追加 - 8状態ステータス表示専用バッジ
 * 統一的な案件ステータス表示を提供するコンポーネント
 *
 * 対応ステータス（8値）:
 * - provisional: 仮受（電話・LINE相談段階）
 * - confirmed: 本受・未配車（正式依頼書受領後）
 * - assigned: 配車済み
 * - picked_up: 引き取り済み
 * - in_transit: 輸送中
 * - delivered: 搬入済み
 * - reported: 報告済み（作業完了報告提出済み）
 * - completed: 完了
 */

import React from 'react';
import { Case } from '@/types';
import { getCaseStatusText, getCaseStatusClass } from '@/utils/statusUtils';

interface CaseStatusBadgeProps {
  /** 案件ステータス（8状態） */
  status: Case['status'] | null | undefined;
  /** バッジサイズ（デフォルト: md） */
  size?: 'sm' | 'md' | 'lg';
  /** 追加のクラス名 */
  className?: string;
  /** ステータステキストを表示するか（デフォルト: true） */
  showLabel?: boolean;
}

/**
 * 案件ステータスバッジコンポーネント
 *
 * 8状態ステータスを視覚的に表現するバッジを表示します。
 * statusUtils の getCaseStatusClass() で定義した色分けを使用します。
 *
 * @example
 * // 基本的な使用例
 * <CaseStatusBadge status="provisional" />
 *
 * @example
 * // サイズ指定
 * <CaseStatusBadge status="in_transit" size="lg" />
 *
 * @example
 * // ラベルなし
 * <CaseStatusBadge status="completed" showLabel={false} />
 */
export const CaseStatusBadge: React.FC<CaseStatusBadgeProps> = ({
  status,
  size = 'md',
  className = '',
  showLabel = true,
}) => {
  // ステータスが null/undefined の場合はデフォルト表示
  if (!status) {
    return (
      <span
        className={`inline-flex items-center px-2 py-1 rounded text-sm font-medium ${
          size === 'sm' ? 'px-1 py-0.5 text-xs' : size === 'lg' ? 'px-3 py-2 text-base' : ''
        } bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300 ${className}`}
        role='status'
        aria-label='未設定'
      >
        未設定
      </span>
    );
  }

  const statusText = getCaseStatusText(status);
  const statusClass = getCaseStatusClass(status);

  return (
    <span
      className={`inline-flex items-center ${
        size === 'sm'
          ? 'px-1.5 py-0.5 text-xs'
          : size === 'lg'
            ? 'px-3 py-2 text-base'
            : 'px-2 py-1 text-sm'
      } rounded font-medium ${statusClass} ${className}`}
      role='status'
      aria-label={statusText}
    >
      {showLabel && <span>{statusText}</span>}
    </span>
  );
};

export default CaseStatusBadge;
